/**
 * Fake cart progress bar + shipping notice (client-side cart totals).
 */
(function () {
  if (!window.FakeCart || window.FakeCartProgress) return;

  function formatMoney(cents) {
    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Money value the free-shipping / gift thresholds are measured against.
   * Uses the AFTER-display-discount subtotal so the bar matches checkout (a
   * discount that pushes the total below the threshold removes free shipping).
   * Falls back to the raw total when the pricing module isn't present, or when
   * no discount is active (in which case the two are equal anyway).
   */
  function getMoneyProgress() {
    if (window.FakeCartPricing && typeof window.FakeCartPricing.getDiscountedSubtotal === "function") {
      return window.FakeCartPricing.getDiscountedSubtotal();
    }
    return window.FakeCart.getTotalPrice();
  }

  function parseProgressConfig(root) {
    const host = root.closest("[data-fake-cart-progress-root]") || root;
    const script = host.querySelector("[data-fake-cart-progress-config]");
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  function getProgressMain(root) {
    if (root.classList.contains("free-product-progress-bar-main")) return root;
    return root.querySelector(".free-product-progress-bar-main");
  }

  function getShippingThreshold(config) {
    if (config.freeShippingThreshold != null) return config.freeShippingThreshold || 0;
    if ((config.shippingMode || "value") === "quantity") return config.freeShippingQty || 0;
    return config.freeShippingCents || 0;
  }

  function getShippingProgress(config) {
    if ((config.shippingMode || "value") === "quantity") {
      return window.FakeCart.getItemCount();
    }
    return getMoneyProgress();
  }

  function formatShippingMissing(config, missing) {
    if ((config.shippingMode || "value") === "quantity") {
      return String(Math.max(0, missing));
    }
    return formatMoney(Math.max(0, missing));
  }

  function buildProgressMessage(config) {
    const shipThreshold = getShippingThreshold(config);
    const giftThreshold = config.freeProductCents || 0;
    const shipProgress = getShippingProgress(config);
    const moneyProgress = getMoneyProgress();
    const shippingMode = config.shippingMode || "value";

    if (shipThreshold <= 0 && giftThreshold <= 0) return "";

    const hasShip = shipThreshold > 0;
    const hasGift = giftThreshold > 0;
    const missingShip = hasShip ? Math.max(0, shipThreshold - shipProgress) : 0;
    const missingGift = hasGift ? Math.max(0, giftThreshold - moneyProgress) : 0;

    const earnedShip = !hasShip || shipProgress >= shipThreshold;
    const earnedGift = !hasGift || moneyProgress >= giftThreshold;

    const title = config.freeProductTitle || "Free Gift";
    const shipAway = (config.shippingAwayText || "").replace(
      "[missingAmount]",
      formatShippingMissing(config, missingShip)
    );
    const giftAway = (config.freeProductAwayText || "")
      .replace("[freeProduct]", title)
      .replace("[missingAmount]", formatMoney(missingGift));
    const shipEarn = config.shippingEarnText || "";
    const giftEarn = (config.freeProductEarnText || "").replace("[freeProduct]", title);

    if (earnedShip && earnedGift) {
      return hasShip ? shipEarn : giftEarn;
    }

    if (shippingMode === "quantity") {
      if (!earnedShip && hasShip) return shipAway;
      if (hasGift && !earnedGift) return giftAway;
      return shipAway;
    }

    if (missingGift > missingShip) {
      if (hasGift && moneyProgress >= shipThreshold && moneyProgress < giftThreshold) {
        return giftAway;
      }
      if (hasShip) return shipAway;
      return giftAway;
    }

    if (hasShip && moneyProgress >= giftThreshold && moneyProgress < shipThreshold) {
      return shipAway;
    }

    return giftAway;
  }

  function getProgressMax(config) {
    const ship = getShippingThreshold(config);
    const gift = config.freeProductCents || 0;
    const shippingMode = config.shippingMode || "value";

    if (shippingMode === "quantity" && ship > 0) return ship;
    if (gift > ship && ship > 0) return gift;
    if (ship > 0) return ship;
    return gift;
  }

  function getProgressValue(config, max) {
    const shippingMode = config.shippingMode || "value";
    if (shippingMode === "quantity") {
      return Math.min(window.FakeCart.getItemCount(), max || 1);
    }
    return Math.min(getMoneyProgress(), max || 1);
  }

  function syncProgressBar(root) {
    const main = getProgressMain(root);
    if (!main) return;

    const config = parseProgressConfig(main);
    if (!config?.enabled) {
      main.hidden = true;
      return;
    }

    main.hidden = false;

    const messageEl = main.querySelector("[data-fake-cart-progress-message], .free-product-progress-bar-successfull");
    const progress = main.querySelector("progress");
    const shipIcon = main.querySelector(".free-shipping-main");
    const giftIcon = main.querySelector(".free-gift-main");

    if (messageEl) {
      messageEl.textContent = buildProgressMessage(config);
    }

    if (progress) {
      const max = getProgressMax(config) || 1;
      progress.max = max;
      progress.value = getProgressValue(config, max);
    }

    const shipThreshold = getShippingThreshold(config);
    const giftThreshold = config.freeProductCents || 0;
    const shipProgress = getShippingProgress(config);
    const moneyProgress = getMoneyProgress();

    if (shipIcon) {
      shipIcon.classList.toggle(
        "free-shipping-main-color",
        shipThreshold > 0 && shipProgress >= shipThreshold
      );
    }

    if (giftIcon) {
      giftIcon.classList.toggle(
        "free-gift-main-color",
        giftThreshold > 0 && moneyProgress >= giftThreshold
      );
    }
  }

  function syncShippingNotice() {
    const notice = document.querySelector("[data-fake-cart-shipping-notice]");
    if (!notice) return;

    const enabled = notice.dataset.enabled === "true";
    const mode = notice.dataset.mode || "quantity";
    const minItems = parseInt(notice.dataset.minItems, 10) || 5;
    const minValueCents = parseInt(notice.dataset.minValueCents, 10) || 0;
    const itemCount = window.FakeCart.getItemCount();
    const totalCents = window.FakeCart.getTotalPrice();
    const isEmpty = window.FakeCart.isEmpty();

    let thresholdMet = false;
    if (mode === "value") {
      thresholdMet = totalCents >= minValueCents;
    } else {
      thresholdMet = itemCount >= minItems;
    }

    notice.hidden = !enabled || isEmpty || !thresholdMet;
  }

  function syncFakeCartProgressUI() {
    document.querySelectorAll("[data-fake-cart-progress-root]").forEach(syncProgressBar);
    syncShippingNotice();
  }

  window.FakeCartProgress = {
    sync: syncFakeCartProgressUI,
    buildProgressMessage,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncFakeCartProgressUI);
  } else {
    syncFakeCartProgressUI();
  }

  document.addEventListener("fake-cart:updated", syncFakeCartProgressUI);
})();
